<template>
    <v-dialog
        v-model="isOpen"
        width="80vw"
        max-width="1200"
        persistent
        :retain-focus="false"
    >
        <v-card class="multicart-confirm-modal">
            <v-card-title class="warning white--text d-flex align-center">
                <v-icon left color="white">mdi-cart-outline</v-icon>
                Artikel in Warenkorbregal einlagern
                <v-spacer></v-spacer>
                <v-btn icon dark @click="closeModal">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            
            <v-card-text class="pa-6 enhanced-content">
                <!-- Slot display -->
                <div class="mb-4">
                    <v-chip class="slot-chip" color="warning" outlined large>
                        <v-icon left>mdi-inbox</v-icon>
                        Fach: {{ multicartPayload?.location || 'Kein Fach' }}
                    </v-chip>
                </div>

                <!-- Article selection -->
                <div>
                    <!-- Current packlist items -->
                    <div class="mb-6">
                        <h3 class="text-h6 mb-4 d-flex align-center">
                            <v-icon left color="warning">mdi-package-variant-closed</v-icon>
                            Artikel zum Einlagern
                            <v-chip v-if="totalSelectedItems > 0" color="success" small class="ml-3">
                                {{ totalSelectedItems }} ausgewählt
                            </v-chip>
                        </h3>

                        <!-- Product Grid -->
                        <div v-if="displayedCurrentItems.length > 0" class="product-grid">
                            <v-card
                                v-for="(item, index) in displayedCurrentItems"
                                :key="`current-${index}`"
                                class="product-item elevation-2"
                                :class="{ 'selected-product': isItemSelected(item, 'current'), 'paired-product': isPairedItem(item) && !isInOriginalPacklist(item) }"
                                @click.stop="toggleCurrentItem(item)"
                            >
                                <!-- Product Image -->
                                <div v-if="getImageUrl(item)" class="product-image-container">
                                    <img :src="getImageUrl(item)" class="product-image" :alt="item.sku" />
                                </div>
                                <div v-else class="product-image-container no-image">
                                    <v-icon size="64" color="grey lighten-1">mdi-image-off</v-icon>
                                </div>

                                <v-card-text class="pa-4">
                                    <div class="d-flex align-center justify-space-between">
                                        <div class="flex-grow-1 mr-3">
                                            <div class="product-sku mb-2">
                                                {{ item.sku }}
                                            </div>

                                            <!-- Paired Badge -->
                                            <div v-if="isPairedItem(item) && !isInOriginalPacklist(item)" class="mb-2">
                                                <v-chip
                                                    x-small
                                                    color="warning"
                                                    dark
                                                >
                                                    <v-icon x-small left>mdi-link-variant</v-icon>
                                                    Gepaart
                                                </v-chip>
                                            </div>

                                            <!-- Quantity Display -->
                                            <div class="quantity-display mb-2">
                                                <v-chip color="success" small dark>
                                                    <v-icon left small>mdi-counter</v-icon>
                                                    Menge: {{ item.qty || item.vqty }}
                                                </v-chip>
                                            </div>
                                        </div>

                                        <!-- Selection Checkbox -->
                                        <div class="selection-area">
                                            <v-checkbox
                                                :input-value="isItemSelected(item, 'current')"
                                                color="success"
                                                class="selection-checkbox ma-0"
                                                hide-details
                                                :ripple="false"
                                                @click.stop
                                            ></v-checkbox>
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </div>

                        <!-- Empty State -->
                        <div v-else class="empty-state">
                            <v-avatar size="64" color="grey lighten-3" class="mb-3">
                                <v-icon size="32" color="grey">mdi-package-variant-remove</v-icon>
                            </v-avatar>
                            <h4 class="text-h6 grey--text mb-2">Keine Artikel verfügbar</h4>
                            <p class="text-body-2 grey--text">
                                In dieser Packliste wurden keine Artikel gefunden.
                            </p>
                        </div>
                    </div>

                    <!-- Other available items -->
                    <div v-if="otherAvailableItems.length > 0">
                        <v-divider class="mb-4"></v-divider>
                        
                        <h3 class="text-h6 mb-4 d-flex align-center">
                            <v-icon left color="secondary">mdi-archive</v-icon>
                            Weitere verfügbare Artikel
                            <v-chip color="info" small class="ml-3">
                                {{ otherAvailableItemsFiltered.length }} verfügbar
                            </v-chip>
                        </h3>

                        <v-select
                            v-model="selectedOtherItems"
                            :items="otherAvailableItemsFiltered"
                            item-text="displayText"
                            item-value="uniqueId"
                            return-object
                            multiple
                            chips
                            deletable-chips
                            label="Weitere Artikel auswählen"
                            hide-details="auto"
                            outlined
                            color="warning"
                            class="other-items-select"
                        >
                            <template v-slot:selection="{ item, index }">
                                <v-chip
                                    v-if="index < 4"
                                    :key="item.uniqueId || item.sku"
                                    small
                                    color="warning"
                                    text-color="white"
                                    close
                                    @click:close="removeOtherItem(item)"
                                >
                                    {{ item.sku }} ({{ item.qty }})
                                </v-chip>
                                <span
                                    v-if="index === 4"
                                    class="grey--text text-caption ml-2"
                                >
                                    (+{{ selectedOtherItems.length - 4 }} weitere)
                                </span>
                            </template>
                            <template v-slot:item="{ item }">
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.displayText }}</v-list-item-title>
                                    <v-list-item-subtitle>Menge: {{ item.qty }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </template>
                        </v-select>
                    </div>
                </div>
            </v-card-text>
            
            <v-divider></v-divider>
            
            <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn @click="closeModal" text color="grey darken-1">
                    Abbrechen
                </v-btn>
                <v-btn
                    @click="confirmStorage"
                    color="warning"
                    :loading="loading"
                    :disabled="totalSelectedItems === 0"
                >
                    <v-icon left>mdi-printer</v-icon>
                    Labeldruck starten
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'MulticartConfirmModal',
    props: {
        packlistName: {
            type: String,
            default: ''
        },
        multicartPayload: {
            type: Object,
            default: null
        },
        currentPacklistItems: {
            type: Array,
            default: () => []
        },
        currentOrder: {
            type: Object,
            default: null
        },
        currentOrderId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            isOpen: true,
            loading: false,
            selectedCurrentItems: [],
            selectedOtherItems: [],
            allowedToPair: ['wmf', 'amefa'],
            pairingSkus: [
                ['LYTO_BRETTCHEN', 'LYTO_AMEFA_WALDFREUNDE'],
                ['LYTO_BRETTCHEN', 'LYTO_AMEFA_BAUERNFREUNDE']
            ]
        }
    },
    computed: {
        filteredCurrentItems() {
            if (!this.multicartPayload || !this.currentOrder) {
                return [];
            }

            // Filter items that are in currentPacklistItems
            const itemsInsidePacklist = this.currentOrder.products.filter(product => {
                // Only include items that are in currentPacklistItems
                const isInCurrentPacklist = this.currentPacklistItems.some(x => x.sku === product.sku);

                return isInCurrentPacklist &&
                       product.isMultiCart === true &&
                       product.status !== 'missing' &&
                       !product.shelf;
            });

            return itemsInsidePacklist;
        },
        displayedCurrentItems() {
            // Combine filteredCurrentItems with selectedOtherItems to show all selected paired items together
            const combined = [...this.filteredCurrentItems];

            // Add items from selectedOtherItems that are paired items
            this.selectedOtherItems.forEach(selectedItem => {
                // Safety check for selectedItem
                if (!selectedItem || !selectedItem.sku) {
                    return;
                }

                // Check if this item is part of a pairing group
                const isPairedItem = this.pairingSkus.some(pairingGroup =>
                    pairingGroup.includes(selectedItem.sku)
                );

                if (isPairedItem && selectedItem.uniqueId) {
                    // Extract index from uniqueId format "SKU-index"
                    const parts = selectedItem.uniqueId.split('-');
                    const index = parseInt(parts[parts.length - 1]);

                    if (!isNaN(index) && index >= 0 && index < this.otherAvailableItems.length) {
                        const originalProduct = this.otherAvailableItems[index];
                        if (originalProduct && !combined.includes(originalProduct)) {
                            combined.push(originalProduct);
                        }
                    }
                }
            });

            return combined;
        },
        otherAvailableItems() {
            if (!this.multicartPayload || !this.currentOrder) {
                return [];
            }

            const otherAvailableItems = this.currentOrder.products.filter(product => {
                return !this.currentPacklistItems.some(x => x.sku === product.sku) &&
                       product.status !== 'missing' &&
                       !product.shelf;
            });

            return otherAvailableItems;
        },
        otherAvailableItemsWithIndex() {
            // Group items by SKU to count duplicates
            const skuCounts = {};
            const skuCounters = {};

            // Count how many of each SKU we have
            this.otherAvailableItems.forEach(item => {
                skuCounts[item.sku] = (skuCounts[item.sku] || 0) + 1;
            });

            // Add display text and unique ID with index
            return this.otherAvailableItems.map((item, index) => {
                const count = skuCounts[item.sku];

                if (count > 1) {
                    // Initialize counter for this SKU if not exists
                    if (!skuCounters[item.sku]) {
                        skuCounters[item.sku] = 0;
                    }
                    skuCounters[item.sku]++;

                    return {
                        ...item,
                        displayText: `${item.sku} (#${skuCounters[item.sku]})`,
                        uniqueId: `${item.sku}-${index}`
                    };
                } else {
                    return {
                        ...item,
                        displayText: item.sku,
                        uniqueId: `${item.sku}-${index}`
                    };
                }
            });
        },
        otherAvailableItemsFiltered() {
            // Filter out paired items that are already selected and shown in displayedCurrentItems
            const selectedPairedItemUniqueIds = new Set();

            this.selectedOtherItems.forEach(selectedItem => {
                // Safety check for selectedItem
                if (!selectedItem || !selectedItem.sku || !selectedItem.uniqueId) {
                    return;
                }

                const isPairedItem = this.pairingSkus.some(pairingGroup =>
                    pairingGroup.includes(selectedItem.sku)
                );

                if (isPairedItem) {
                    selectedPairedItemUniqueIds.add(selectedItem.uniqueId);
                }
            });

            // Filter out selected paired items
            return this.otherAvailableItemsWithIndex.filter(item =>
                !selectedPairedItemUniqueIds.has(item.uniqueId)
            );
        },
        totalSelectedItems() {
            return this.selectedCurrentItems.length + this.selectedOtherItems.length;
        }
    },
    methods: {
        closeModal() {
            this.isOpen = false;
            this.$emit('close-modal');
        },
        toggleCurrentItem(item) {
            // Use object reference comparison
            const index = this.selectedCurrentItems.findIndex(selected => selected === item);

            if (index > -1) {
                this.selectedCurrentItems.splice(index, 1);
            } else {
                this.selectedCurrentItems.push(item);
            }
        },
        isItemSelected(item, type) {
            if (type === 'current') {
                // First check if it's in selectedCurrentItems
                const inCurrentItems = this.selectedCurrentItems.some(selected => selected === item);
                if (inCurrentItems) {
                    return true;
                }

                // Also check if it's a paired item in selectedOtherItems
                // Find the index in otherAvailableItems to match with wrapped items
                const indexInOtherItems = this.otherAvailableItems.findIndex(otherItem => otherItem === item);
                if (indexInOtherItems !== -1) {
                    const targetUniqueId = `${item.sku}-${indexInOtherItems}`;
                    return this.selectedOtherItems.some(selected =>
                        selected.uniqueId === targetUniqueId
                    );
                }
            }
            return false;
        },
        isPairedItem(item) {
            // Check if this item's SKU is part of any pairing group
            return this.pairingSkus.some(pairingGroup =>
                pairingGroup.includes(item.sku)
            );
        },
        isInOriginalPacklist(item) {
            // Check if this item is in the original filteredCurrentItems
            return this.filteredCurrentItems.some(originalItem => originalItem === item);
        },
        removeOtherItem(item) {
            // Use object reference comparison
            const index = this.selectedOtherItems.findIndex(selected => selected === item);
            if (index > -1) {
                this.selectedOtherItems.splice(index, 1);
            }
        },

        async confirmStorage() {
            this.loading = true;
            
            try {
                const allSelectedItems = [...this.selectedCurrentItems, ...this.selectedOtherItems];
                
                // Since all selected items are from the current order, get all packlist item IDs for this order
                const packlistItemIds = this.currentPacklistItems
                    .filter(packlistItem => packlistItem.orderId === this.currentOrderId)
                    .map(packlistItem => packlistItem.id);

                const payload = {
                    location: this.multicartPayload?.location,
                    items: allSelectedItems,
                    packlistItemIds: packlistItemIds
                };
                
                // Emit the confirmation event with the selected items
                this.$emit('confirm-storage', payload);
            } catch (error) {
                console.error('Error confirming storage:', error);
            } finally {
                this.loading = false;
            }
        },
        getImageUrl(product) {
            // Check if product has attributes
            if (!product.attributes || typeof product.attributes !== 'object') {
                return null;
            }

            let hasDesignId = product.designId || product.attributes.designId;
            let hasPreview = product.attributes.preview;

            if (hasDesignId) {
                return `https://accelery-poster.s3.eu-central-1.amazonaws.com/${encodeURIComponent(hasDesignId)}.png`;
            } else if (hasPreview) {
                return hasPreview[0].url || hasPreview;
            } else {
                let foundImageUrl = null;
                for (const [key, value] of Object.entries(product.attributes)) {
                    if (typeof value === 'string' && value.startsWith('http')) {
                        foundImageUrl = value;
                        break;
                    }
                }

                return foundImageUrl;
            }
        },
        resetSelections() {
            this.selectedCurrentItems = [];
            this.selectedOtherItems = [];
        },
        preselectCurrentPacklistItems() {
            // Pre-select all items from the current order (they are already filtered by orderId)
            this.selectedCurrentItems = [...this.filteredCurrentItems];
        }, 
        preselectPairedItems() {
            console.log('Preselecting paired items for', this.packlistName);

            if (!this.currentOrder) {
                return;
            }

            // Find which pairing group matches the order
            for (const pairingGroup of this.pairingSkus) {
                console.log('Checking pairing group:', pairingGroup);

                // Count how many of each SKU in the pairing group we have in the order
                const skuCounts = {};
                pairingGroup.forEach(sku => {
                    const count = this.currentOrder.products.filter(product =>
                        product.sku === sku &&
                        product.isMultiCart === true &&
                        product.status !== 'missing' &&
                        !product.shelf
                    ).length;
                    skuCounts[sku] = count;
                });

                console.log('SKU counts in order:', skuCounts);

                // Check if ALL SKUs in the pairing group have at least one item
                const allSkusAvailable = pairingGroup.every(sku => skuCounts[sku] > 0);

                if (allSkusAvailable) {
                    console.log('All SKUs from pairing group are available, selecting ALL matching items');

                    // Get ALL products from the order that match ANY of the pairing SKUs
                    const pairedProducts = this.currentOrder.products.filter(product =>
                        pairingGroup.includes(product.sku) &&
                        product.isMultiCart === true &&
                        product.status !== 'missing' &&
                        !product.shelf
                    );

                    console.log(`Found ${pairedProducts.length} paired products:`, pairedProducts.map(p => p.sku));

                    // Add ALL paired products to appropriate selection arrays
                    pairedProducts.forEach(product => {
                        // Check if product is in filteredCurrentItems
                        const isInCurrentItems = this.filteredCurrentItems.some(item => item === product);
                        const isAlreadySelectedCurrent = this.selectedCurrentItems.some(item => item === product);

                        console.log(`Processing ${product.sku}: isInCurrentItems=${isInCurrentItems}, isAlreadySelectedCurrent=${isAlreadySelectedCurrent}`);

                        // If it's in filteredCurrentItems, add to selectedCurrentItems
                        if (isInCurrentItems && !isAlreadySelectedCurrent) {
                            console.log(`Adding ${product.sku} to selectedCurrentItems`);
                            this.selectedCurrentItems.push(product);
                        }
                        // If it's not in filteredCurrentItems, find it in otherAvailableItems and get wrapped version
                        else if (!isInCurrentItems) {
                            // Find the index in otherAvailableItems
                            const indexInOtherItems = this.otherAvailableItems.findIndex(item => item === product);

                            if (indexInOtherItems !== -1) {
                                // We need to find the matching wrapped item from otherAvailableItemsWithIndex
                                // The uniqueId format is SKU-index, so we search by that
                                const targetUniqueId = `${product.sku}-${indexInOtherItems}`;

                                // Schedule the selection to happen after the computed properties update
                                this.$nextTick(() => {
                                    const wrappedItem = this.otherAvailableItemsWithIndex.find(item =>
                                        item && item.uniqueId === targetUniqueId
                                    );

                                    if (wrappedItem) {
                                        const isAlreadySelectedOther = this.selectedOtherItems.some(item =>
                                            item.uniqueId === wrappedItem.uniqueId
                                        );

                                        if (!isAlreadySelectedOther) {
                                            console.log(`Adding ${wrappedItem.displayText} to selectedOtherItems`);
                                            this.selectedOtherItems.push(wrappedItem);
                                        }
                                    }
                                });
                            }
                        }
                    });

                    console.log('Paired SKUs preselected:', pairedProducts.map(p => p.sku));
                    console.log('selectedCurrentItems:', this.selectedCurrentItems.map(p => p.sku));
                    console.log('selectedOtherItems:', this.selectedOtherItems.map(p => p.sku));
                    break; // Only match one pairing group
                }
            }
        }
    },
    watch: {
        isOpen(newVal) {
            if (!newVal) {
                this.$emit('close-modal');
            } else {
                // Reset selections when modal opens
                this.resetSelections();
                // Pre-select current packlist items
                this.$nextTick(() => {
                    this.preselectCurrentPacklistItems();
                });
            }
        },
        filteredCurrentItems: {
            handler() {
                // Update preselection when items are loaded
                this.$nextTick(() => {
                    this.preselectCurrentPacklistItems();
                });
            },
            immediate: true
        }
    },
    mounted() {
        // Pre-select current packlist items when component mounts
        this.$nextTick(() => {
            this.preselectCurrentPacklistItems();

            if (this.packlistName && this.allowedToPair.some(brand => this.packlistName.toLowerCase().includes(brand))) {
                this.preselectPairedItems();
            }
        });
    }
}
</script>

<style scoped>
.multicart-confirm-modal {
    border-radius: 16px;
    overflow: hidden;
}

.enhanced-content {
    background-color: #fafafa;
    min-height: 200px;
}

.slot-chip {
    font-weight: 600;
}

/* Product Grid Layout */
.product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
}

/* Product Card Styles */
.product-item {
    transition: all 0.3s ease;
    border-radius: 12px;
    border: 2px solid transparent;
    cursor: pointer;
    position: relative;
    background-color: white;
}

.product-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
    border-color: rgba(76, 175, 80, 0.3);
}

.selected-product {
    border-color: #4CAF50;
    background: linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(76, 175, 80, 0.02) 100%);
    box-shadow: 0 4px 16px rgba(76, 175, 80, 0.3) !important;
}

.selected-product::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 32px 32px 0;
    border-color: transparent #4CAF50 transparent transparent;
    z-index: 1;
}

/* Paired product styling */
.paired-product {
    position: relative;
}

.paired-product::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 32px 0 0 32px;
    border-color: transparent transparent transparent #FF9800;
    z-index: 1;
}

/* Product Image Container */
.product-image-container {
    width: 100%;
    height: 200px;
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
}

.product-image-container.no-image {
    background-color: #fafafa;
}

.product-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.product-sku {
    font-family: 'Roboto Mono', monospace;
    font-size: 1rem;
    font-weight: 600;
    color: #1976d2;
    background-color: #e3f2fd;
    padding: 4px 12px;
    border-radius: 6px;
    display: inline-block;
}

.quantity-display {
    display: flex;
    align-items: center;
}

.selection-area {
    display: flex;
    align-items: center;
    justify-content: center;
}

.selection-checkbox {
    transform: scale(1.5);
}

/* Empty State */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 200px;
    padding: 40px;
}

/* Other Items Select */
.other-items-select {
    background-color: white;
}

.selection-summary {
    display: flex;
    align-items: center;
}

.v-btn {
    text-transform: none;
    font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 900px) {
    .product-grid {
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 12px;
    }
}

@media (max-width: 600px) {
    .product-grid {
        grid-template-columns: 1fr;
    }
}

/* Smooth animations */
.product-item:active {
    transform: translateY(0px) scale(0.98);
    transition: transform 0.1s ease;
}
</style>